import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGlobalFontSize } from '../context/GlobalFontSizeContext';
import { useUser } from '../context/UserContext';

const CONTACT_FORM_ENDPOINT =
  'https://us-central1-bloom-website-a10b5.cloudfunctions.net/sendContactForm';
const ACTION_REQUIRED_PREFIX = '[ACTION REQUIRED]';

const CONTACT_CONTEXT_TEMPLATES = {
  'partner-sales': {
    subject: 'Partner plan inquiry',
    message:
      'Hi BloomIQ team,\n\nI would like to discuss the Partner plan for my organization. Please contact me with pricing details, onboarding steps, and recommended setup.\n\nThank you.',
  },
};

const ContactUs = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { fontSize } = useGlobalFontSize();
  const { userDetails, email } = useUser();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const contactContext = queryParams.get('context') || '';
  const actionRequired = queryParams.get('actionRequired') === '1';

  useEffect(() => {
    const contextTemplate = CONTACT_CONTEXT_TEMPLATES[contactContext];
    const fullName = [userDetails?.name, userDetails?.surname].filter(Boolean).join(' ').trim();

    setFormData((prev) => {
      const nextData = { ...prev };

      if (!nextData.name && fullName) {
        nextData.name = fullName;
      }
      if (!nextData.email && email) {
        nextData.email = email;
      }
      if (contextTemplate) {
        if (!nextData.subject) {
          nextData.subject = contextTemplate.subject;
        }
        if (!nextData.message) {
          nextData.message = contextTemplate.message;
        }
      }

      return nextData;
    });
  }, [contactContext, userDetails?.name, userDetails?.surname, email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const shouldPrefixActionRequired =
      actionRequired &&
      !String(formData.subject || '').toUpperCase().startsWith(ACTION_REQUIRED_PREFIX);

    const payload = {
      ...formData,
      subject: shouldPrefixActionRequired
        ? `${ACTION_REQUIRED_PREFIX} ${formData.subject}`
        : formData.subject,
    };

    try {
      const response = await fetch(CONTACT_FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus({ type: 'success', message: t('contact.success') });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus({ type: 'error', message: t('contact.errorSend') });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus({ type: 'error', message: t('contact.errorConnect') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full min-h-full bg-[#F0F5F2] bg-dark-app">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1
            className="font-['Inter'] font-medium text-bloom-green mb-4"
            style={{ fontSize: fontSize + 10 }}
          >
            {t('contact.heading')}
          </h1>
          <p
            className="font-['Inter'] font-normal text-[#282A29] dark:text-[#f0f5f2]"
            style={{ fontSize: fontSize + 2 }}
          >
            {t('contact.subheading')}
          </p>
        </div>

        {submitStatus && (
          <div
            className={`p-4 mb-6 rounded ${
              submitStatus.type === 'success'
                ? 'bg-green-100 border border-green-400 text-green-700'
                : 'bg-red-100 border border-red-400 text-red-700'
            }`}
          >
            {submitStatus.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-[#5D625F] dark:text-[#828382]"
            >
              {t('contact.name')}
            </label>
            <input
              type="text"
              name="name"
              id="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md bg-[#f9f9f9] dark:bg-[#1d1d1d] border border-[#C3C4C3] dark:border-[#282A29] text-[#282A29] dark:text-[#f0f5f2] px-4 py-2 focus:outline-none focus:ring-2 focus:ring-bloom-green focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-[#5D625F] dark:text-[#828382]"
            >
              {t('contact.email')}
            </label>
            <input
              type="email"
              name="email"
              id="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md bg-[#f9f9f9] dark:bg-[#1d1d1d] border border-[#C3C4C3] dark:border-[#282A29] text-[#282A29] dark:text-[#f0f5f2] px-4 py-2 focus:outline-none focus:ring-2 focus:ring-bloom-green focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="subject"
              className="block text-sm font-medium text-[#5D625F] dark:text-[#828382]"
            >
              {t('contact.subject')}
            </label>
            <input
              type="text"
              name="subject"
              id="subject"
              required
              value={formData.subject}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md bg-[#f9f9f9] dark:bg-[#1d1d1d] border border-[#C3C4C3] dark:border-[#282A29] text-[#282A29] dark:text-[#f0f5f2] px-4 py-2 focus:outline-none focus:ring-2 focus:ring-bloom-green focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-[#5D625F] dark:text-[#828382]"
            >
              {t('contact.message')}
            </label>
            <textarea
              name="message"
              id="message"
              rows={6}
              required
              value={formData.message}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md bg-[#f9f9f9] dark:bg-[#1d1d1d] border border-[#C3C4C3] dark:border-[#282A29] text-[#282A29] dark:text-[#f0f5f2] px-4 py-2 focus:outline-none focus:ring-2 focus:ring-bloom-green focus:border-transparent"
            />
          </div>

          <div className="text-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-bloom-green hover:bg-bloom-green-hover transition-colors ${
                isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? t('contact.sending') : t('contact.sendMessage')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactUs;
